// Clinical Assessment Rubric data extracted from D3 and D4 rubric PDFs.
// Both rubrics share 3 categories scored 1-4, with year-specific descriptors.

export const SCORE_LEVELS = [
  { score: 4, label: 'Excellence', shortLabel: '4', color: '#4CAF50' },
  { score: 3, label: 'Acceptable', shortLabel: '3', color: '#2196F3' },
  { score: 2, label: 'Modification Necessary', shortLabel: '2', color: '#FF9800' },
  { score: 1, label: 'Unacceptable (D)', shortLabel: 'D/1', color: '#f44336' }
];

export const CATEGORIES = [
  {
    key: 'professionalism',
    label: 'Professionalism, Patient & Appointment Management',
    shortLabel: 'Professionalism',
    fieldName: 'Professionalism Score'
  },
  {
    key: 'problemSolving',
    label: 'Problem Solving, Clinical Reasoning & Integration of Relevant Scientific Evidence',
    shortLabel: 'Problem Solving',
    fieldName: 'Problem Solving Score'
  },
  {
    key: 'clinicalSkill',
    label: 'Clinical Skill, Infection Control, & Pain Management',
    shortLabel: 'Clinical Skill',
    fieldName: 'Clinical Skill Score'
  }
];

export const RUBRIC_DATA = {
  D3: {
    professionalism: {
      4: [
        'Outstanding preparation, record management, & time utilization',
        'High level of empathy, compassion, & respect for patient, staff & faculty',
        'Clearly recognizes patient\'s needs',
        'Skilled & purposeful communication (includes empathy & listening)',
        'Actively seeks & is receptive to feedback'
      ],
      3: [
        'Acceptable preparation, record management, & time utilization (minimal guidance needed)',
        'Demonstrates empathy, compassion, & respect for patient, staff & faculty',
        'Recognizes patient\'s needs (minimal guidance needed)',
        'Acceptable communication',
        'Seeks feedback',
        'Student was prepared to perform procedure, needs minimal guidance, finishes on time'
      ],
      2: [
        'Minimally prepared, help needed with record management or time management (guidance needed)',
        'Does not fully recognize or understand interpersonal needs of patient, staff or faculty',
        'May lack empathy, compassion, or respect for patient, staff & faculty',
        'Challenged communication',
        'Does not seek feedback',
        'Standard only met with assistance/modification'
      ],
      1: [
        'Unprepared & unaware of the steps & procedure to satisfactorily meet needs of the patient',
        'Inappropriate record management or time utilization',
        'Ineffective communication & failure to establish functional rapport',
        'Does not recognize patient\'s needs',
        'Disrespectful, lacks empathy, or compassion',
        'Defensive to critical feedback',
        'Standard was not met'
      ]
    },
    problemSolving: {
      4: [
        'Outstanding conceptual understanding & insightful application of relevant scientific evidence',
        'Engages & valuably contributes to collaborative discussion / Seeks more information',
        'When guidance is requested, appropriately & accurately informs faculty & proposes excellent treatment options based on relevant scientific evidence'
      ],
      3: [
        'Acceptable level of conceptual understanding & insightful application of relevant scientific evidence (minimal guidance needed)',
        'Seeks more information & asks insightful questions',
        'When guidance is requested, appropriately informs faculty & proposes treatment options with minor gaps in understanding'
      ],
      2: [
        'Gaps in understanding, clinical reasoning & problem solving',
        'Foundation knowledge is incomplete or inaccurate',
        'Requires coaching & guidance, frequent & direct check-ins/supervision',
        'Minimal scientific evidence incorporated into patient treatment'
      ],
      1: [
        'Failed to demonstrate conceptual understanding, clinical reasoning, problem solving & application of relevant scientific evidence',
        'Cannot execute without direct oversight, detailed instruction & coaching',
        'May require additional coursework'
      ]
    },
    clinicalSkill: {
      4: [
        'Independence: Student carried out procedure with indirect supervision (D3)',
        'Progressing/approaching a level that can be trusted to execute independently (limited supervision)',
        'Outstanding technical skill, infection control, & pain management demonstrated'
      ],
      3: [
        'Independence: Student carried out procedure with direct supervision, guidance, & verification',
        'Acceptable technical skill, infection control, & pain management demonstrated/appropriate for first three semesters',
        'Follows faculty instruction'
      ],
      2: [
        'Independence: Student carried out procedure with direct supervision and minimal intervention',
        'Lacks expected level of skill & independence for the first three semesters',
        'Additional coaching & instruction required / May require additional coursework',
        'Clinical skill below acceptable level',
        'Minor deviation due to student error (treatment errors require minor additional treatment or variation in planned treatment)'
      ],
      1: [
        'Independence: Student unable to complete procedure at an acceptable level',
        'Did not follow faculty instruction or proceeded with treatment beyond expected norm without informing faculty',
        'Did not understand their limitations',
        'Treatment errors require additional treatment or a change in treatment',
        'Additional coursework may be needed'
      ]
    }
  },
  D4: {
    professionalism: {
      4: [
        'Outstanding & independent preparation, record management, & time utilization',
        'High level of empathy, compassion, & respect for patient, staff & faculty',
        'Clearly recognizes patient\'s needs',
        'Skilled & purposeful communication (includes empathy & listening)',
        'Actively seeks & is receptive to feedback'
      ],
      3: [
        'Acceptable preparation, record management, & time utilization (minimal guidance needed)',
        'Demonstrates empathy, compassion, & respect for patient, staff & faculty',
        'Recognizes patient\'s needs (minimal guidance needed)',
        'Acceptable communication',
        'Seeks feedback',
        'Student was prepared to perform procedure, needs minimal guidance, finishes on time'
      ],
      2: [
        'Minimally prepared, help needed with record management or time management (guidance needed)',
        'Does not fully recognize or understand interpersonal needs of patient, staff or faculty',
        'May lack empathy, compassion, or respect for patient, staff & faculty',
        'Challenged communication',
        'Does not seek feedback',
        'Standard only met with assistance/modification'
      ],
      1: [
        'Unprepared & unaware of the steps & procedure to satisfactorily meet needs of the patient',
        'Inappropriate record management or time utilization',
        'Ineffective communication & failure to establish functional rapport',
        'Does not recognize patient\'s needs',
        'Disrespectful, lacks empathy, or compassion',
        'Defensive to critical feedback',
        'Standard was not met'
      ]
    },
    problemSolving: {
      4: [
        'Outstanding conceptual understanding & insightful application of relevant scientific evidence',
        'Engages & valuably contributes to collaborative discussion',
        'When guidance is requested, appropriately & accurately informs faculty & proposes excellent treatment options based on relevant scientific evidence'
      ],
      3: [
        'Acceptable level of conceptual understanding & insightful application of relevant scientific evidence (minimal guidance needed)',
        'Asks insightful questions related to scientific evidence',
        'When guidance is requested, appropriately informs faculty & proposes treatment options with minor gaps in understanding'
      ],
      2: [
        'Gaps in understanding, clinical reasoning & problem solving',
        'Foundation knowledge is incomplete or inaccurate',
        'Requires coaching & guidance, frequent & direct check-ins/supervision',
        'Minimal scientific evidence incorporated into patient treatment'
      ],
      1: [
        'Failed to demonstrate conceptual understanding, clinical reasoning, problem solving & application of relevant scientific evidence',
        'Cannot execute without direct oversight, detailed instruction & coaching',
        'May require additional coursework'
      ]
    },
    clinicalSkill: {
      4: [
        'Independence: Student can be trusted to execute with limited supervision, qualified to work independently',
        'Outstanding technical skill, infection control, & pain management demonstrated',
        'Clinical procedures accomplished independently and competently'
      ],
      3: [
        'Independence: Student can be trusted to execute with mostly indirect supervision & verification with minimal faculty intervention',
        'Approaching a level that can be trusted to execute independently',
        'Acceptable technical skill, infection control, & pain management appropriate for last three semesters',
        'Follows faculty instruction'
      ],
      2: [
        'Independence: Requires mostly direct supervision with minimal faculty intervention',
        'Minimal faculty intervention may be necessary to complete treatment or get patient to acceptable point for dismissal',
        'Minor deviation due to student error (treatment errors require minor additional treatment or variation in planned treatment)',
        'Clinical procedures accomplished with considerable instruction'
      ],
      1: [
        'Independence: Student cannot be trusted to execute & may require additional coursework',
        'Full faculty intervention necessary to complete treatment or get patient to acceptable point for dismissal',
        'Failed to demonstrate acceptable technical skills, infection control, & acceptable pain management',
        'Does not follow faculty instruction or proceeds beyond expected norm without informing faculty',
        'Does not understand their limitations',
        'Treatment errors require additional treatment or a change in treatment'
      ]
    }
  }
};

export const LEVEL_DESCRIPTIONS = {
  4: 'Demonstrates Excellence & Independence',
  3: 'Minimal Guidance Needed / Acceptable Outcome',
  2: 'Guidance & Minimal Intervention Necessary',
  1: 'Unable to Achieve Acceptable Outcome'
};

export const getDescriptors = (yearLevel, categoryKey, score) => {
  const yearData = RUBRIC_DATA[yearLevel];
  if (!yearData) return [];
  const categoryData = yearData[categoryKey];
  if (!categoryData) return [];
  return categoryData[score] || [];
};
